import React, { useEffect, useState } from 'react';
import { api, parseReceiptExtras, type Receipt } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { StatusBadge } from '../components/ui/StatusBadge';
import { VendorTile } from '../components/ui/VendorTile';
import { Button } from '../components/ui/Button';
import { FieldInput, FieldSelect } from '../components/ui/FieldInput';
import { Arrow, Check, Sparkle, XeroX, X } from '../components/ui/Icons';
import { UpgradeModal } from '../components/UpgradeModal';

function fmtDate(d: string | null): string {
  if (!d) return '--';
  const dt = new Date(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
}

function isoToDmy(iso: string | null): string {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function dmyToIso(dmy: string): string {
  const parts = dmy.split('-');
  if (parts.length !== 3) return dmy;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

function confidenceLevel(c: number | null): ConfidenceLevel {
  if (c == null) return 'none';
  if (c >= 0.85) return 'high';
  if (c >= 0.6) return 'medium';
  return 'low';
}

function confidenceColor(level: ConfidenceLevel): string {
  if (level === 'high') return T.sage;
  if (level === 'medium') return T.warn;
  if (level === 'low') return T.fail;
  return T.graphiteDim;
}

export function ReceiptDetail({ id }: { id: string }) {
  const isMobile = useIsMobile();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'ok' | 'error' } | null>(null);
  const [accounts, setAccounts] = useState<Array<{ AccountID: string; Code: string; Name: string; Type: string }>>([]);
  const [quotaHit, setQuotaHit] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    api.getReceipt(id).then((r) => setReceipt(r.receipt));
    api.xeroAccounts().then((r) => setAccounts(r.accounts.filter((a) => a.Type === 'EXPENSE' || a.Type === 'DIRECTCOSTS'))).catch(() => {});
  }, [id]);

  if (!receipt) {
    return <div style={{ padding: 40, color: T.graphiteDim, fontSize: 14 }}>Loading...</div>;
  }

  function field<K extends keyof Receipt>(key: K, value: Receipt[K]) {
    setReceipt((r) => (r ? { ...r, [key]: value } : r));
  }

  async function save() {
    if (!receipt) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.confirmReceipt(receipt.id, {
        vendor: receipt.vendor,
        receipt_date: receipt.receipt_date,
        amount: receipt.amount,
        tax: receipt.tax,
        currency: receipt.currency,
        description: receipt.description,
        account_code: receipt.account_code,
      });
      setReceipt(res.receipt);
      setMessage({ text: 'Saved.', type: 'ok' });
    } catch (err) {
      setMessage({ text: (err as Error).message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function post() {
    if (!receipt) return;
    if (receipt.status === 'posted' || receipt.status === 'reconciled') return;
    setSaving(true);
    setMessage(null);
    try {
      await api.postReceipt(receipt.id);
      const fresh = await api.getReceipt(receipt.id);
      setReceipt(fresh.receipt);
      setMessage({ text: 'Posted to Xero.', type: 'ok' });
      setQuotaHit(false);
    } catch (err) {
      const msg = (err as Error).message;
      // API wrapper throws `${status}: ${body}`. Detect 402 quota_exceeded.
      if (msg.startsWith('402:') || msg.includes('quota_exceeded')) {
        setQuotaHit(true);
        setMessage({ text: 'Free quota reached — subscribe to continue posting.', type: 'error' });
      } else {
        setMessage({ text: msg, type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  }

  async function retry() {
    if (!receipt) return;
    await api.retryReceipt(receipt.id);
    const fresh = await api.getReceipt(receipt.id);
    setReceipt(fresh.receipt);
  }

  async function removeFromXero() {
    if (!receipt) return;
    if (!confirm('Remove this transaction from Xero? The receipt will need to be re-posted.')) return;
    setSaving(true);
    try {
      await api.removeFromXero(receipt.id);
      const fresh = await api.getReceipt(receipt.id);
      setReceipt(fresh.receipt);
      setMessage({ text: 'Removed from Xero.', type: 'ok' });
    } catch (err) {
      setMessage({ text: (err as Error).message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this receipt?')) return;
    await api.deleteReceipt(receipt!.id);
    window.location.hash = '#/';
  }

  const conf = confidenceLevel(receipt.confidence);
  const confColor = confidenceColor(conf);
  const isPosted = receipt.status === 'posted' || receipt.status === 'reconciled';
  const extras = parseReceiptExtras(receipt);
  const alternateAccount = extras.account_code_alternate && extras.account_code_alternate !== receipt.account_code
    ? accounts.find((a) => a.Code === extras.account_code_alternate) ?? null
    : null;

  const aiHintBlock = (extras.category_intent || extras.reasoning || alternateAccount) ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: T.navy, borderRadius: 10, marginTop: 8 }}>
      {extras.category_intent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.creamDim }}>
          <Sparkle size={12} color={T.sage} />
          <span>AI category: <span style={{ color: T.cream, fontWeight: 500 }}>{extras.category_intent}</span></span>
        </div>
      )}
      {extras.reasoning && (
        <div style={{ fontSize: 12, color: T.creamDim, lineHeight: 1.5 }}>{extras.reasoning}</div>
      )}
      {alternateAccount && (
        <button
          onClick={() => field('account_code', alternateAccount.Code)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            borderRadius: 100,
            background: 'rgba(168,199,158,0.18)',
            color: T.sage,
            fontSize: 12,
            fontWeight: 500,
            border: `1px solid rgba(168,199,158,0.35)`,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Also consider: {alternateAccount.Code} · {alternateAccount.Name}
        </button>
      )}
    </div>
  ) : null;

  const quotaCta = quotaHit ? (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 150,
        background: T.navy,
        color: T.cream,
        borderTop: `1px solid ${T.navyLine2}`,
        padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        boxShadow: '0 -12px 32px rgba(10,20,40,0.35)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <Sparkle size={16} color={T.sage} />
        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
          <div style={{ fontWeight: 600 }}>Free quota reached</div>
          <div style={{ color: T.creamDim, fontSize: 12 }}>
            Subscribe to unlock posting and higher monthly limits.
          </div>
        </div>
      </div>
      <Button variant="sage" size="small" onClick={() => setUpgradeOpen(true)}>
        Subscribe to unlock posting
      </Button>
    </div>
  ) : null;

  const upgradeModal = (
    <UpgradeModal
      open={upgradeOpen}
      onClose={() => setUpgradeOpen(false)}
      reason={quotaHit ? 'Free quota reached' : undefined}
    />
  );

  const traceSteps = [
    {
      label: 'Extracted',
      detail: receipt.vendor ? `"${receipt.vendor}"` : 'Pending',
      level: receipt.vendor ? conf : 'none' as ConfidenceLevel,
    },
    {
      label: 'Matched account',
      detail: receipt.account_code ?? 'Not matched',
      level: receipt.account_code ? conf : 'none' as ConfidenceLevel,
    },
    {
      label: 'GST calculated',
      detail: receipt.tax != null ? `$${receipt.tax.toFixed(2)}` : 'N/A',
      level: receipt.tax != null ? conf : 'none' as ConfidenceLevel,
    },
    {
      label: 'Xero action',
      detail: isPosted ? 'Posted' : (receipt.status === 'confirmed' ? 'Ready' : 'Pending'),
      level: isPosted ? 'high' as ConfidenceLevel : 'none' as ConfidenceLevel,
    },
  ];

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        {/* Back link */}
        <a
          href="#/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            color: T.graphiteDim,
            textDecoration: 'none',
            marginBottom: 12,
            minHeight: 44,
          }}
        >
          <Arrow size={14} style={{ transform: 'rotate(180deg)' }} />
          Back
        </a>

        {/* Compact header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <VendorTile vendor={receipt.vendor ?? '?'} size={40} />
            <div style={{ minWidth: 0 }}>
              <h1
                style={{
                  ...sx.serif,
                  fontSize: 22,
                  color: T.graphite,
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {receipt.vendor ?? '(unknown vendor)'}
              </h1>
              <StatusBadge status={receipt.status as any} />
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <span style={{ ...sx.mono, fontSize: 20, fontWeight: 600, color: T.graphite }}>
              {receipt.amount != null ? `${receipt.currency} ${receipt.amount.toFixed(2)}` : '--'}
            </span>
            {receipt.confidence != null && (
              <div style={{ ...sx.mono, fontSize: 12, color: confColor, fontWeight: 500 }}>
                {(receipt.confidence * 100).toFixed(0)}%
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {receipt.error_message && (
          <div
            style={{
              padding: '12px 16px',
              background: T.failBg,
              borderRadius: 10,
              marginBottom: 12,
              fontSize: 13,
              color: T.fail,
              lineHeight: 1.5,
            }}
          >
            {receipt.error_message}
          </div>
        )}

        {/* AI extraction trace - vertical stack */}
        <div
          style={{
            background: T.navy,
            borderRadius: 14,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Sparkle size={14} color={T.sage} />
            <span style={{ ...sx.label, color: T.creamDim }}>AI extraction trace</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {traceSteps.map((step, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ ...sx.label, color: T.creamDim, flex: 'none' }}>{step.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: confidenceColor(step.level),
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ ...sx.mono, fontSize: 13, color: T.cream }}>
                    {step.detail}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Editable fields - single column */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 14,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput
              label="Vendor"
              value={receipt.vendor ?? ''}
              onChange={(v) => field('vendor', v)}
            />
            <FieldInput
              label="Date (DD-MM-YYYY)"
              value={isoToDmy(receipt.receipt_date)}
              onChange={(v) => field('receipt_date', dmyToIso(v))}
            />
            <FieldInput
              label="Currency"
              value={receipt.currency ?? ''}
              onChange={(v) => field('currency', v)}
            />
            <FieldInput
              label="Amount"
              value={receipt.amount?.toString() ?? ''}
              onChange={(v) => field('amount', parseFloat(v) || 0)}
              mono
            />
            <FieldInput
              label="GST"
              value={receipt.tax?.toString() ?? ''}
              onChange={(v) => field('tax', parseFloat(v) || 0)}
              mono
            />
            {accounts.length > 0 ? (
              <FieldSelect
                label="Account"
                value={receipt.account_code ?? ''}
                onChange={(v) => field('account_code', v)}
                options={accounts.map((a) => ({ value: a.Code, label: `${a.Code} - ${a.Name}` }))}
                placeholder="-- Select account --"
              />
            ) : (
              <FieldInput
                label="Account code"
                value={receipt.account_code ?? ''}
                onChange={(v) => field('account_code', v)}
              />
            )}
            {aiHintBlock}
            <FieldInput
              label="Description"
              value={receipt.description ?? ''}
              onChange={(v) => field('description', v)}
            />
          </div>
        </div>

        {/* Xero info */}
        {isPosted && receipt.xero_transaction_id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <XeroX size={13} color={T.xeroBlue} />
            <span style={{ fontSize: 12, color: T.graphiteDim }}>In Xero</span>
          </div>
        )}

        <p style={{ fontSize: 12, color: T.graphiteDim, fontStyle: 'italic', marginBottom: 16 }}>
          Corrections teach the AI to remember this mapping.
        </p>

        {/* Action buttons - full width stacked */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button variant="sage" onClick={post} disabled={saving || isPosted} style={{ width: '100%', minHeight: 48 }}>
            <Check size={14} />
            Post to Xero
          </Button>
          <Button variant="navy" onClick={save} disabled={saving} style={{ width: '100%', minHeight: 48 }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {isPosted && (
            <Button variant="ghostLight" onClick={removeFromXero} disabled={saving} style={{ width: '100%', minHeight: 48 }}>
              Remove from Xero
            </Button>
          )}
          {receipt.status === 'failed' && (
            <Button variant="ghostLight" onClick={retry} style={{ width: '100%', minHeight: 48 }}>
              Retry
            </Button>
          )}
          <Button variant="delete" onClick={handleDelete} style={{ width: '100%', minHeight: 48 }}>
            Delete
          </Button>
        </div>

        {/* Status message */}
        {message && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 13,
              background: message.type === 'ok' ? '#E8F5E3' : T.failBg,
              color: message.type === 'ok' ? T.sageDarker : T.fail,
            }}
          >
            {message.text}
          </div>
        )}

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />

        {/* Extra bottom spacing when quota CTA is docked */}
        {quotaHit && <div style={{ height: 96 }} />}

        {quotaCta}
        {upgradeModal}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Back link */}
      <a
        href="#/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          color: T.graphiteDim,
          textDecoration: 'none',
          marginBottom: 16,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = T.graphite; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = T.graphiteDim; }}
      >
        <Arrow size={14} style={{ transform: 'rotate(180deg)' }} />
        All receipts
      </a>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <VendorTile vendor={receipt.vendor ?? '?'} size={48} />
        <div style={{ flex: 1 }}>
          <h1 style={{ ...sx.serif, fontSize: 28, color: T.graphite, margin: 0 }}>
            {receipt.vendor ?? '(unknown vendor)'}
          </h1>
        </div>
        <StatusBadge status={receipt.status as any} />
        {receipt.confidence != null && (
          <span
            style={{
              ...sx.mono,
              fontSize: 13,
              color: confColor,
              fontWeight: 500,
            }}
          >
            {(receipt.confidence * 100).toFixed(0)}%
          </span>
        )}
        <span style={{ ...sx.mono, fontSize: 22, fontWeight: 600, color: T.graphite }}>
          {receipt.amount != null ? `${receipt.currency} ${receipt.amount.toFixed(2)}` : '--'}
        </span>
      </div>

      {/* Error message */}
      {receipt.error_message && (
        <div
          style={{
            padding: '12px 16px',
            background: T.failBg,
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 13,
            color: T.fail,
            lineHeight: 1.5,
          }}
        >
          {receipt.error_message}
        </div>
      )}

      {/* AI extraction trace panel */}
      <div
        style={{
          background: T.navy,
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Sparkle size={14} color={T.sage} />
          <span style={{ ...sx.label, color: T.creamDim }}>AI extraction trace</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
          }}
        >
          {traceSteps.map((step, i) => (
            <div key={i}>
              <div style={{ ...sx.label, color: T.creamDim, marginBottom: 6 }}>{step.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: confidenceColor(step.level),
                    flexShrink: 0,
                  }}
                />
                <span style={{ ...sx.mono, fontSize: 13, color: T.cream }}>
                  {step.detail}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editable fields */}
      <div
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.paperLine}`,
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FieldInput
            label="Vendor"
            value={receipt.vendor ?? ''}
            onChange={(v) => field('vendor', v)}
            style={{ gridColumn: '1 / -1' }}
          />
          <FieldInput
            label="Date (DD-MM-YYYY)"
            value={isoToDmy(receipt.receipt_date)}
            onChange={(v) => field('receipt_date', dmyToIso(v))}
          />
          <FieldInput
            label="Currency"
            value={receipt.currency ?? ''}
            onChange={(v) => field('currency', v)}
          />
          <FieldInput
            label="Amount"
            value={receipt.amount?.toString() ?? ''}
            onChange={(v) => field('amount', parseFloat(v) || 0)}
            mono
          />
          <FieldInput
            label="GST"
            value={receipt.tax?.toString() ?? ''}
            onChange={(v) => field('tax', parseFloat(v) || 0)}
            mono
          />
          {accounts.length > 0 ? (
            <FieldSelect
              label="Account"
              value={receipt.account_code ?? ''}
              onChange={(v) => field('account_code', v)}
              options={accounts.map((a) => ({ value: a.Code, label: `${a.Code} - ${a.Name}` }))}
              placeholder="-- Select account --"
              style={{ gridColumn: '1 / -1' }}
            />
          ) : (
            <FieldInput
              label="Account code"
              value={receipt.account_code ?? ''}
              onChange={(v) => field('account_code', v)}
              style={{ gridColumn: '1 / -1' }}
            />
          )}
          {aiHintBlock && <div style={{ gridColumn: '1 / -1' }}>{aiHintBlock}</div>}
          <FieldInput
            label="Description"
            value={receipt.description ?? ''}
            onChange={(v) => field('description', v)}
            style={{ gridColumn: '1 / -1' }}
          />
        </div>
      </div>

      {/* Footer bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        {/* Left side: link to Xero + info */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          {isPosted && receipt.xero_transaction_id && (
            <span style={{ fontSize: 12, color: T.graphiteDim, display: 'flex', alignItems: 'center', gap: 4 }}>
              <XeroX size={13} color={T.xeroBlue} />
              <span>In Xero</span>
            </span>
          )}
          <span style={{ fontSize: 12, color: T.graphiteDim, fontStyle: 'italic' }}>
            Corrections teach the AI to remember this mapping.
          </span>
        </div>

        {/* Right side: action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="delete" size="small" onClick={handleDelete}>
            Delete
          </Button>

          {isPosted && (
            <Button variant="ghostLight" size="small" onClick={removeFromXero} disabled={saving}>
              Remove from Xero
            </Button>
          )}

          {receipt.status === 'failed' && (
            <Button variant="ghostLight" size="small" onClick={retry}>
              Retry
            </Button>
          )}

          <Button variant="navy" size="small" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>

          <Button
            variant="sage"
            size="small"
            onClick={post}
            disabled={saving || isPosted}
          >
            <Check size={14} />
            Post to Xero
          </Button>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            background: message.type === 'ok' ? '#E8F5E3' : T.failBg,
            color: message.type === 'ok' ? T.sageDarker : T.fail,
          }}
        >
          {message.text}
        </div>
      )}

      {/* Extra bottom spacing when quota CTA is docked */}
      {quotaHit && <div style={{ height: 96 }} />}

      {quotaCta}
      {upgradeModal}
    </div>
  );
}
